/**
 * <count-up> — anime les chiffres de la section "Résultats prouvés" de 0 vers leur valeur
 * finale quand ils entrent dans le viewport. Dégradable : si IntersectionObserver est absent,
 * si l'utilisateur préfère les mouvements réduits, ou si la valeur n'est pas numérique,
 * le texte final reste affiché tel quel.
 */
class CountUp extends HTMLElement {
  connectedCallback() {
    this.targets = Array.from(this.querySelectorAll('[data-countup-target]'));
    if (!this.targets.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;

    this.targets.forEach((el) => {
      el.dataset.countupFinal = el.textContent;
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.targets.forEach((el) => this.animate(el));
        });
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  animate(el) {
    const raw = el.dataset.countupFinal != null ? el.dataset.countupFinal : el.textContent;
    const match = String(raw).match(/-?\d+(?:[.,]\d+)?/);
    if (!match) return; // pas de nombre (ex. texte seul) → on laisse tel quel

    const numStr = match[0];
    const usesComma = numStr.indexOf(',') !== -1;
    const end = parseFloat(numStr.replace(',', '.'));
    if (!isFinite(end)) return;

    const decimals = (numStr.split(/[.,]/)[1] || '').length;
    const prefix = raw.slice(0, match.index);
    const suffix = raw.slice(match.index + numStr.length);
    const duration = 1100;
    let start = null;

    const frame = (timestamp) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      let current = (end * eased).toFixed(decimals);
      if (usesComma) current = current.replace('.', ',');
      el.textContent = prefix + current + suffix;
      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = raw;
      }
    };

    el.textContent = prefix + (usesComma ? '0'.replace('.', ',') : '0') + suffix;
    requestAnimationFrame(frame);
  }
}

if (!customElements.get('count-up')) {
  customElements.define('count-up', CountUp);
}
